/**
 * AgentFeedbackReportsList — "Your reports about the agent" (RA-FB-REPORTED, BR-24).
 *
 * The landlord's own reports, each labelled "Reported", with Withdraw while the report is still
 * open. Reports are created by an improvement conversation that escalates a defect and are not
 * editable; a conversation-created report says so and can reopen its conversation
 * (IMP-BR-6: the only non-start caller of openConversation). This component NEVER renders a status
 * (triaged, resolved, ...): the landlord view doesn't carry one. Hidden when the landlord has no
 * reports.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { asIsoDateTimeString } from '@resida/shared';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  caseFeedbackQueryKey,
  listCaseFeedback,
  withdrawFeedback,
  type LandlordFeedbackView,
} from '../../api/agent-feedback.api';
import { deriveTimezone, formatTimestamp } from '../../utils/date';
import { useImprovementConversationOpener } from './improvement/ImprovementConversationProvider';

const KIND_SUMMARY: Record<LandlordFeedbackView['behaviorKind'], string> = {
  commission: "Did something it shouldn't have",
  omission: "Didn't do something it should have",
  sequencing: 'Did something too early / out of order',
  content: 'Said the wrong thing',
  tone: 'Said it the wrong way',
  escalation: "Asked me when it shouldn't / didn't when it should",
  ignored_playbook: "Didn't follow my playbook",
  other: 'Something else',
};

function summarize(report: LandlordFeedbackView): string {
  const capability = report.actionX.capability;
  const action = report.actionX.text ?? (capability ? capability.replace(/[_.]/g, ' ') : null);
  const kind = KIND_SUMMARY[report.behaviorKind];
  return action ? `${kind} — ${action}` : kind;
}

export interface AgentFeedbackReportsListProps {
  caseId: string;
  timezone?: string;
}

export function AgentFeedbackReportsList({ caseId, timezone: timezoneProp }: AgentFeedbackReportsListProps) {
  const timezone = timezoneProp ?? deriveTimezone('');
  const queryClient = useQueryClient();
  const openConversation = useImprovementConversationOpener();
  const { data } = useQuery({
    queryKey: caseFeedbackQueryKey(caseId),
    queryFn: () => listCaseFeedback(caseId),
    staleTime: 30_000,
  });
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reports = data?.reports ?? [];
  if (reports.length === 0) return null;

  async function handleWithdraw(id: string) {
    setBusyId(id);
    setError(null);
    try {
      await withdrawFeedback(id);
      setConfirmingId(null);
      await queryClient.invalidateQueries({ queryKey: caseFeedbackQueryKey(caseId) });
    } catch {
      setError("We couldn't withdraw your report. Try again.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section
      aria-labelledby="agent-feedback-reports-heading"
      className="rounded-xl border border-border bg-card p-4 md:p-5"
      data-testid="agent-feedback-reports"
    >
      <h2 id="agent-feedback-reports-heading" className="m-0 mb-3 text-base font-semibold text-foreground">
        Your reports about the agent
      </h2>
      {error && (
        <p role="alert" className="m-0 mb-2 text-sm text-destructive" data-testid="agent-feedback-reports-error">
          {error}
        </p>
      )}
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {reports.map((report) => (
          <li
            key={report.id}
            data-testid={`agent-feedback-report-${report.id}`}
            className="flex flex-col gap-1 rounded-md border border-border px-3 py-2"
          >
            <div className="flex items-start gap-3">
              <Badge variant="info" className="shrink-0">Reported</Badge>
              <span className="min-w-0 flex-1 text-sm text-foreground">{summarize(report)}</span>
              {report.fromConversation && (
                <Badge variant="primary" className="shrink-0" data-testid={`agent-feedback-from-conversation-${report.id}`}>
                  From a conversation
                </Badge>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-sm text-muted-foreground">
              {formatTimestamp(asIsoDateTimeString(report.createdAt), timezone)}
            </span>
            {report.conversationId && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="min-h-[44px] text-sm"
                onClick={() => openConversation({ conversationId: report.conversationId! })}
                data-testid={`agent-feedback-open-conversation-${report.id}`}
              >
                Open conversation
              </Button>
            )}
            {report.withdrawable &&
              (confirmingId === report.id ? (
                <span className="flex flex-wrap items-center gap-2" role="group" aria-label="Confirm withdraw">
                  <span className="text-sm text-foreground">Withdraw this report?</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    className="min-h-[44px] text-sm"
                    disabled={busyId === report.id}
                    onClick={() => void handleWithdraw(report.id)}
                    data-testid={`agent-feedback-withdraw-confirm-${report.id}`}
                  >
                    {busyId === report.id ? 'Withdrawing…' : 'Withdraw'}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="min-h-[44px] text-sm"
                    disabled={busyId === report.id}
                    onClick={() => setConfirmingId(null)}
                    data-testid={`agent-feedback-withdraw-cancel-${report.id}`}
                  >
                    Keep
                  </Button>
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="min-h-[44px] text-sm"
                    onClick={() => setConfirmingId(report.id)}
                    data-testid={`agent-feedback-withdraw-${report.id}`}
                  >
                    Withdraw
                  </Button>
                </span>
              ))}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default AgentFeedbackReportsList;
